// BacktestFields.tsx — the Backtest scenario form fields, grouped into the same
// collapsible <details> sections as ForecastFields (Sparplan / Fonds & Steuer
// default-open, Rendite & Kosten closed). Requires a real fund (reads cached market
// data); date-range hints come from the fund's coverage window.
import { useId } from 'preact/hooks';
import { FundPicker } from './FundPicker.js';
import { NumberField } from './NumberField.js';
import { ToggleField } from './ToggleField.js';
import { useExplain } from './Hint.js';
import { applyFundToBacktestForm } from '../lib/scenario.js';
import type { BacktestFormState } from '../lib/scenario.js';
import type { FundInfo } from '../lib/types.js';
import { formatDateDe } from '../lib/format.js';

export interface BacktestFieldsProps {
  form: BacktestFormState;
  onChange: (form: BacktestFormState) => void;
  funds: FundInfo[];
  fundsLoading?: boolean | undefined;
  fundsError?: string | null | undefined;
}

export function BacktestFields({ form, onChange, funds, fundsLoading, fundsError }: BacktestFieldsProps) {
  const startId = useId();
  const endId = useId();
  const showExplanations = useExplain();
  const patch = (p: Partial<BacktestFormState>) => onChange({ ...form, ...p });
  const fund = funds.find((f) => f.isin === form.fundIsin) ?? null;

  return (
    <div>
      <details class="details-section" open>
        <summary>Sparplan</summary>
        <div class="section-body">
          <div class="field">
            <label for={startId}>Von</label>
            <input
              id={startId}
              type="date"
              value={form.startDate}
              onInput={(e) => patch({ startDate: (e.currentTarget as HTMLInputElement).value })}
            />
          </div>

          <div class="field">
            <label for={endId}>Bis</label>
            <input
              id={endId}
              type="date"
              value={form.endDate}
              onInput={(e) => patch({ endDate: (e.currentTarget as HTMLInputElement).value })}
            />
          </div>

          <NumberField
            label="Ausführungstag im Monat"
            value={form.executionDayOfMonth}
            onChange={(v) => patch({ executionDayOfMonth: Math.round(v) })}
            min={1}
            max={28}
            step={1}
          />

          <NumberField
            label="Monatliche Rate"
            unit="€"
            value={form.monthlyEur}
            onChange={(v) => patch({ monthlyEur: v })}
            min={0}
            max={3000}
            step={10}
          />

          <NumberField
            label="Sparplan-Dynamik (jährl. Erhöhung)"
            unit="%"
            value={form.contributionStepUpPercent}
            onChange={(v) => patch({ contributionStepUpPercent: v })}
            min={0}
            max={20}
            step={0.1}
            sliderMax={10}
            sliderStep={0.5}
            hint={
              showExplanations
                ? 'Erhöht die monatliche Rate automatisch, jeweils nach einem vollen Jahr ab Sparplanbeginn (Feld „Von“).'
                : undefined
            }
          />

          <NumberField
            label="Einmalerlag"
            unit="€"
            value={form.lumpSumEur}
            onChange={(v) => patch({ lumpSumEur: v })}
            min={0}
            max={200000}
            step={100}
            sliderScale="money"
          />

          <NumberField
            label="Ausführungsgebühr"
            unit="€ / Ausführung"
            value={form.executionFeeEur}
            onChange={(v) => patch({ executionFeeEur: v })}
            min={0}
            max={20}
            step={0.1}
          />

          <ToggleField
            label="Verkauf am Ende"
            checked={form.sellAtEnd}
            onChange={(v) => patch({ sellAtEnd: v })}
          />
        </div>
      </details>

      <details class="details-section" open>
        <summary>Fonds &amp; Steuer</summary>
        <div class="section-body">
          <FundPicker
            funds={funds}
            value={form.fundIsin}
            allowCustom={false}
            loading={fundsLoading}
            error={fundsError}
            onChange={(isin) => onChange(applyFundToBacktestForm(form, funds.find((f) => f.isin === isin) ?? null))}
          />

          {fund ? (
            <p class="field-hint">
              Kursdaten: {formatDateDe(fund.coverage.priceFrom)}–{formatDateDe(fund.coverage.priceTo)} (
              {fund.coverage.priceCount} Kurse) · Meldungen: {formatDateDe(fund.coverage.meldungFrom)}–
              {formatDateDe(fund.coverage.meldungTo)} ({fund.coverage.meldungCount})
              {fund.coverage.meldungCount === 0 ? ' — AGE-Backtest wird deaktiviert sein.' : ''}
              {' '}Zeitraum „Von“/„Bis“ wurde beim Auswählen auf die verfügbare Kurshistorie gesetzt (sofern noch
              nicht manuell geändert).
            </p>
          ) : null}
        </div>
      </details>

      <details class="details-section">
        <summary>Rendite &amp; Kosten</summary>
        <div class="section-body">
          <ToggleField
            label="TER-Schätzung angeben"
            checked={form.terBpEstimatePercent !== null}
            onChange={(v) => patch({ terBpEstimatePercent: v ? (fund?.terBp != null ? fund.terBp / 100 : 0.2) : null })}
            hint="Nur informativ (D-007): reale Kurse enthalten die TER bereits; der Wert dient nur der gestrichelten Wasserfall-Anzeige."
          />

          {form.terBpEstimatePercent !== null ? (
            <NumberField
              label="TER-Schätzung"
              unit="%"
              value={form.terBpEstimatePercent}
              onChange={(v) => patch({ terBpEstimatePercent: v })}
              min={0}
              max={2}
              step={0.01}
            />
          ) : null}
        </div>
      </details>
    </div>
  );
}
